import React, { useRef, useEffect, useState } from 'react';
import styles from './Component/CSS/ViewMore.module.css';
import { Play } from 'lucide-react';
import { useAudioPlayer } from '../components/audio/AudioPlayerContext';
import { useSubscriptionCheck } from '../hooks/useSubscriptionCheck';

const Episodes = ({ seriesId, chapters, seriesData }) => {
  const scrollContainerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Use the global audio player context
  const { currentAudio, playAudio, isPlaying } = useAudioPlayer();
  const { checkSubscriptionBeforePlay } = useSubscriptionCheck();

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const maxScroll = container.scrollWidth - container.clientWidth;
      const currentScroll = container.scrollLeft;
      const progress = (currentScroll / maxScroll) * 100;
      setScrollProgress(progress);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Check if an episode is currently playing
  const isEpisodePlaying = (chapter) => {
    return currentAudio && currentAudio.id === chapter.id;
  };

  // Handle episode click
  const handleEpisodeClick = (chapter) => {
    // Check subscription before playing
    if (!checkSubscriptionBeforePlay()) {
      return;
    }

    // Format chapter data for the audio player
    const audioData = {
      id: chapter.id,
      title: chapter.audio_title || `Episode ${chapter.chapter_number}`,
      author: seriesData?.creator?.name || "Unknown Author",
      song_url: chapter.audio_url,
      image: chapter.audio_computer_image || chapter.audio_mobile_image || seriesData?.coverImageUrl,
      duration: chapter.audio_duration
    };
    
    // Play the audio using the global context
    playAudio(audioData);
  };

  // Format duration from seconds to minutes:seconds
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  if (!chapters || chapters.length === 0) {
    return <div className={styles.noEpisodes}>No episodes available</div>;
  }

  return (
    <div className={styles.mainContainer}>
      <h2 className={styles.title}>Episodes</h2>
      <div className={styles.outerContainer}>
        <div className={styles.episodesContainer} ref={scrollContainerRef}>
          <div className={styles.episodesGrid}>
            {chapters.map((chapter) => (
              <div
                key={chapter.id}
                className={`${styles.episodeCard} ${isEpisodePlaying(chapter) ? styles.selected : ''}`}
                onClick={() => handleEpisodeClick(chapter)}
                role="button"
                tabIndex={0}
              >
                <div className={styles.imageContainer}>
                  <img
                    src={chapter.audio_computer_image || chapter.audio_mobile_image || '/default-episode.jpg'}
                    alt={chapter.audio_title || `Episode ${chapter.chapter_number}`}
                    className={styles.episodeImage}
                  />
                  <div className={styles.playOverlay}>
                    {isEpisodePlaying(chapter) && isPlaying ? (
                      <div className={styles.nowPlayingIndicator}>
                        <div className={styles.bar}></div>
                        <div className={styles.bar}></div>
                        <div className={styles.bar}></div>
                      </div>
                    ) : (
                      <Play size={24} color={isEpisodePlaying(chapter) ? '#ff0000' : 'white'} />
                    )}
                  </div>
                </div>
                <div className={styles.episodeInfo}>
                  <h3 className={styles.episodeTitle}>
                    Episode {chapter.chapter_number}
                  </h3>
                  <p className={styles.episodeSubtitle}>
                    {chapter.audio_title || chapter.ebook_title || 'Untitled'}
                  </p>
                  <div className={styles.episodeDetails}>
                    <span className={styles.episodeDuration}>
                      {formatDuration(chapter.audio_duration)}
                    </span>
                    <span className={styles.episodeType}>
                      {chapter.type?.charAt(0).toUpperCase() + chapter.type?.slice(1) || 'Audio'}
                    </span>
                    {isEpisodePlaying(chapter) && (
                      <span className={styles.nowPlaying}>Now Playing</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.scrollTrack}>
          <div className={styles.scrollBar} style={{ width: `${scrollProgress}%` }}></div>
        </div>
      </div>
    </div>
  );
};

export default Episodes;